import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

import type { FacetGroup } from '@src/facets';
import type {
  CatalogRuleConditionGroup,
  ConditionItem,
  LogicalOperator,
} from './types';
import { createEmptyCondition, createEmptyConditionGroup, isConditionGroup } from './types';
import ConditionRow from './ConditionRow';

interface ConditionGroupCardProps {
  group: CatalogRuleConditionGroup;
  isFirst: boolean;
  depth: number;
  facetGroups: FacetGroup[];
  onChange: (id: string, updates: Partial<CatalogRuleConditionGroup>) => void;
  onDelete: (id: string) => void;
  onSiblingOperatorChange: (newOperator: LogicalOperator) => void;
}

const ConditionGroupCard = ({
  group,
  isFirst,
  depth,
  facetGroups,
  onChange,
  onDelete,
  onSiblingOperatorChange,
}: ConditionGroupCardProps) => {
  const handleChildChange = (childId: string, updates: Partial<ConditionItem>) => {
    onChange(group.id, {
      children: group.children.map(child =>
        child.id === childId ? { ...child, ...updates } as ConditionItem : child,),
    });
  };

  const handleChildDelete = (childId: string) => {
    onChange(group.id, {
      children: group.children.filter(child => child.id !== childId),
    });
  };

  const handleChildOperatorChange = (newOperator: LogicalOperator) => {
    onChange(group.id, {
      matchType: newOperator === 'or' ? 'any' : 'all',
      children: group.children.map(child => ({ ...child, logicalOperator: newOperator })),
    });
  };

  const handleAddRule = () => {
    onChange(group.id, {
      children: [...group.children, createEmptyCondition()],
    });
  };

  const handleAddGroup = () => {
    onChange(group.id, {
      children: [...group.children, createEmptyConditionGroup()],
    });
  };

  return (
    <Box mb={1.5}>
      <Box display="flex" alignItems="flex-start" gap={1.5}>
        {isFirst ? (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ minWidth: 80, mt: 1, color: 'text.primary', whiteSpace: 'nowrap' }}
          >
            WHEN
          </Typography>
        ) : (
          <TextField
            select
            size="small"
            value={group.logicalOperator}
            onChange={e => onSiblingOperatorChange(e.target.value as LogicalOperator)}
            sx={{ minWidth: 80, maxWidth: 80 }}
            InputProps={{ sx: { bgcolor: 'background.paper' } }}
          >
            <MenuItem value="and">and</MenuItem>
            <MenuItem value="or">or</MenuItem>
          </TextField>
        )}

        <Paper
          variant="outlined"
          sx={{ flex: 1, p: 2, bgcolor: depth % 2 === 0 ? 'grey.50' : 'background.paper' }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              {group.matchType === 'any' ?
                'Any of the following rules match:' :
                'All of the following rules match:'}
            </Typography>
            <IconButton
              size="small"
              onClick={() => onDelete(group.id)}
              sx={{ color: 'text.secondary' }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Box>

          {group.children.map((child, i) =>
            isConditionGroup(child) ? (
              <ConditionGroupCard
                key={child.id}
                group={child}
                isFirst={i === 0}
                depth={depth + 1}
                facetGroups={facetGroups}
                onChange={handleChildChange}
                onDelete={handleChildDelete}
                onSiblingOperatorChange={handleChildOperatorChange}
              />
            ) : (
              <ConditionRow
                key={child.id}
                condition={child}
                isFirst={i === 0}
                facetGroups={facetGroups}
                onChange={handleChildChange}
                onDelete={handleChildDelete}
                onSiblingOperatorChange={handleChildOperatorChange}
              />
            ),)}

          <Box display="flex" gap={1} mt={1}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddRule}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Add Rule
            </Button>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddGroup}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Add Rule Group
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ConditionGroupCard;
